import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AlertCircle } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("No session_id received from Google. Please try again.");
      return;
    }
    const session_id = decodeURIComponent(m[1]);
    let pendingRole = null;
    let orgCode = null;
    try { pendingRole = localStorage.getItem("hikmah_pending_role"); } catch (err) { console.debug("[auth-callback] pending-role read failed:", err); }
    try { orgCode = localStorage.getItem("hwh_org"); } catch (err) { console.debug("[auth-callback] org-code read failed:", err); }
    (async () => {
      try {
        const r = await api.post("/auth/google/session", {
          session_id,
          ...(pendingRole ? { role: pendingRole } : {}),
          ...(orgCode ? { org_code: orgCode } : {}),
        });
        try { localStorage.removeItem("hikmah_pending_role"); } catch (err) { console.debug("[auth-callback] pending-role clear failed:", err); }
        try { localStorage.removeItem("hwh_org"); } catch (err) { console.debug("[auth-callback] org-code clear failed:", err); }
        setUser(r.data.user);
        // Clear the hash so a refresh doesn't re-trigger the callback
        window.history.replaceState(null, "", "/app");
        navigate("/app", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail
          || e?.message
          || "Couldn't complete Google sign-in. Please try again.";
        console.error("Google session exchange failed:", e);
        setError(detail);
      }
    })();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen geo-bg grid place-items-center p-6">
        <div className="hifz-card max-w-md text-center" data-testid="auth-callback-error">
          <div className="mx-auto h-12 w-12 rounded-full bg-terracotta-500/15 text-terracotta-600 grid place-items-center">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display font-semibold text-xl text-forest-700">Sign-in didn't complete</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Link to="/login" replace className="mt-6 btn-primary inline-flex" data-testid="back-to-login">
            Back to sign-in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen geo-bg grid place-items-center">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="animate-pulse-soft font-display text-forest-700 text-lg">Signing you in…</div>
        <p className="mt-2 text-sm text-muted-foreground">Establishing your secure session</p>
      </div>
    </div>
  );
}
